const URN_PREFIX = "urn:ruuby";

const uuidRegex = new RegExp("^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$");

export enum UrnResource {
  THERAPIST = "therapist",
  BOOKING = "booking",
  CUSTOMER = "customer",
  CUSTOMER_CREDIT_ADJUSTMENT = "customer-credit-adjustment",
  ADDRESS = "address",
  ADMIN_NOTE = "admin-note",
  AVAILABILITY = "availability",
  BOOKING_TRANSACTION = "booking-transaction",
  BOOKING_TREATMENT = "booking-treatment",
  TREATMENT = "treatment",
  TREATMENT_CATEGORY = "treatment-category",
  TREATMENT_LIST = "treatment-list",
  SALON = "salon",
  USER = "user",
  REFUND = "refund",
  CARD = "card",
  PROMOTION = "promotion",
  REVIEW = "review",
  DISTRICT = "district",
  OPERATOR = "operator",
}

/**
 * Create an URN for given resource and id
 * @param resource Resource of URN to create
 * @param identifier ID to use in urn
 */
export function convert(resource: UrnResource, identifier: string[]): string {
  const suffix = identifier.join("-");

  return `${URN_PREFIX}:${resource}:${suffix}`;
}

/**
 * Return the id part of a given URN
 * @param resource Resource of thr URN
 * @param urn URN to extract the ID from
 */
export function extractId(resource: UrnResource, urn: string): string[] {
  // eslint-disable-next-line @rushstack/security/no-unsafe-regexp
  const urnRegex = new RegExp(`${URN_PREFIX}:${resource}:(.+)$`);

  const match = urn.match(urnRegex);
  if (match === null) {
    throw new Error(`URN ${urn} is not compatible with resource ${resource}`);
  }

  const uuidMatch = match[1].match(uuidRegex);

  if (uuidMatch !== null) {
    // is a uuid
    return [match[1]];
  } else {
    return match[1].split("-");
  }
}

/**
 * Return the resource part of a given URN
 * @param urn URN to extract the resource part form
 */
export function extractResource(urn: string): UrnResource {
  // eslint-disable-next-line @rushstack/security/no-unsafe-regexp
  const resourceRegex = new RegExp(`^${URN_PREFIX}:(.+):.+$`);
  const match = urn.match(resourceRegex);
  if (!match?.length) {
    throw new Error(`Could not extract resource from urn of: ${urn}`);
  }

  return match[1] as UrnResource;
}

/**
 * Return URN without leading dashes
 * @param urn URN to extract the resource part form
 */
export function normalize(urn: string): string {
  return urn.replace(/-[0-9]+$/, "");
}

/**
 * The function `isValidUrn` checks if a given URN is valid for a given resource type.
 * @param {UrnResource} resource - The `resource` parameter is of type `UrnResource`. It represents the
 * type of resource that the URN belongs to. It could be any valid value that represents a specific
 * resource type, such as "user", "product", "order", etc.
 * @param {string} urn - The `urn` parameter is a string that represents a Uniform Resource Name (URN).
 * @returns The function `isValidUrn` returns a boolean value. It returns `true` if the `extractId`
 * function does not throw an error when called with the `resource` and `urn` arguments, indicating
 * that the `urn` is valid. It returns `false` if an error is thrown, indicating that the `urn` is not
 * valid.
 */
export const isValidUrn = (resource: UrnResource, urn: string): boolean => {
  try {
    extractId(resource, urn);
    return true;
  } catch (e) {
    return false;
  }
};
